import React, { useState, useEffect } from "react";

import toast from "react-hot-toast";
import api, { API_URL } from '../../api'
import DashboardContainer from "../../components/dashboard/DashboardContainer";
import "../../assets/css/notification/notification.css"
import "../../assets/css/notification/notification-list.css"
import "../../assets/css/notification/notification-select-all.css"
import { PrimaryButton, SecondaryButton } from "../../components/forms/buttons";
import Cookies from 'js-cookie';
import notificationService from '../../services/notificationService';
import NotificationList from '../../components/notifications/NotificationList';
import EmptyState from '../../components/common/EmptyState';

function formatNotificationDate(inputDate) {
  const date = new Date(inputDate);
  const now = new Date();

  const differenceInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  const options = { hour: 'numeric', minute: 'numeric', hour12: true };
  const time = date.toLocaleTimeString('en-US', options);

  if (differenceInDays === 0) {
    return `Today, ${time}`;
  } else if (differenceInDays === 1) {
    return `Yesterday, ${time}`;
  } else {
    const monthDay = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return `${monthDay}, ${time}`;
  }
}


const NotificationPageContent = ({ updateUnreadCount }) => {
  const [notifications, setNotifications] = useState([]);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [allNotifications, setAllNotifications] = useState([]);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const isAllSelected = isSelectingAll;

  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (!dataLoaded) {
      const fetchNotifications = async () => {
        try {
          const response = await api.get(`${API_URL}/notifications/?page_size=20`);
          let fetchedNotifications = response.data.results;

          // Get user's subscription settings
          const subscriptions = notificationService.getLocalSubscriptions();

          // Filter notifications based on subscribed categories
          const filteredNotifications = fetchedNotifications.filter(notification => {
            const category = notification.category;
            // If no subscriptions data, show all notifications
            if (Object.keys(subscriptions).length === 0) return true;
            // Show notification if category is subscribed (default to true if not set)
            return subscriptions[category] !== false;
          });

          setNotifications(filteredNotifications);
          setNextPage(response.data.next);
          setPreviousPage(response.data.previous);

          setTimeout(async () => {
            try {
              const allResponse = await api.get(`${API_URL}/notifications/?page_size=1000`);
              let allFetched = allResponse.data.results;

              // Apply same filtering to all notifications
              const filteredAll = allFetched.filter(notification => {
                const category = notification.category;
                if (Object.keys(subscriptions).length === 0) return true;
                return subscriptions[category] !== false;
              });

              setAllNotifications(filteredAll);
            } catch (error) {
              console.error("Failed to load all notifications:", error);
            }
          }, 200);

          const unreadCount = filteredNotifications.filter(n => !n.is_read).length;
          setUnreadCount(unreadCount);
          updateUnreadCount(unreadCount);
          setDataLoaded(true);
        } catch (error) {
          console.error("Failed to load notifications:", error);
          toast.error("Failed to load notifications");
        }
      };

      fetchNotifications();
    }
  }, [dataLoaded, updateUnreadCount]);

  const handleMarkAsRead = async () => {
    let ids;
    if (isSelectingAll) {
      ids = allNotifications.map(n => n.id);
    } else {
      ids = selectedNotifications.map(index => notifications[index]?.id).filter(Boolean);
    }
    
    if (ids.length === 0) {
      toast.error("No notifications selected for marking as read.");
      return;
    }
    
    try {
      await api.post(
        `${API_URL}/notifications/mark_as_read/`, {
        ids: ids,
        is_read: true
      });
      
      if (isSelectingAll) {
        setAllNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      } else {
        setNotifications(prev =>
          prev.map(n => ids.includes(n.id) ? { ...n, is_read: true } : n)
        );
      }
      
      const newUnreadCount = isSelectingAll
        ? 0
        : notifications.filter(n => !ids.includes(n.id) && !n.is_read).length;
      setUnreadCount(newUnreadCount);
      updateUnreadCount(newUnreadCount);
      toast.success("Marked as read");
      // Remove window.location.reload() to avoid unnecessary page refresh
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      toast.error("Failed to update notifications");
    }
  };

  const handleDelete = async () => {
    let ids;
    if (isSelectingAll) {
      ids = allNotifications.map(n => n.id);
    } else {
      ids = selectedNotifications.map(index => notifications[index]?.id).filter(Boolean);
    }

    if (ids.length === 0) {
      toast.error("No notifications selected for deletion.");
      return;
    }

    try {
      await api.post(`${API_URL}/notifications/delete/`, { ids });

      if (isSelectingAll) {
        setAllNotifications([]);
        setNotifications([]);
        setUnreadCount(0);
        updateUnreadCount(0);
      } else {
        const updatedNotifications = notifications.filter(n => !ids.includes(n.id));
        setNotifications(updatedNotifications);
        const newUnreadCount = updatedNotifications.filter(n => !n.is_read).length;
        setUnreadCount(newUnreadCount);
        updateUnreadCount(newUnreadCount);
      }

      toast.success("Notifications deleted successfully");
      // Remove window.location.reload() to avoid unnecessary page refresh
    } catch (error) {
      console.error("Error deleting notifications:", error);
      toast.error("Failed to delete notifications");
    }
  };

  const handleDeleteAll = async () => {
    const confirmed = window.confirm("Are you sure you want to delete all notifications? This action cannot be undone.");
    if (!confirmed) return;

    const ids = allNotifications.map(n => n.id);
    if (ids.length === 0) {
      toast.error("No notifications to delete.");
      return;
    }

    try {
      await api.post(`${API_URL}/notifications/delete/`, { ids });
      setAllNotifications([]);
      setNotifications([]);
      setUnreadCount(0);
      updateUnreadCount(0);
      toast.success("All notifications deleted successfully");
      // Remove window.location.reload() to avoid unnecessary page refresh
    } catch (error) {
      console.error("Error deleting all notifications:", error);
      toast.error("Failed to delete all notifications");
    }
  };

  const handleSelectAll = () => {
    setIsSelectingAll(!isSelectingAll);
    if (!isSelectingAll) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications([]);
    }
  };

  const handleSelect = (index) => {
    if (isSelectingAll) {
      setIsSelectingAll(false);
    }
    
    setSelectedNotifications(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const changePage = async (url, direction) => {
    if (!url) return;

    try {
      // Extract the relative path from the full URL to avoid protocol mismatch
      const urlObj = new URL(url);
      const relativePath = urlObj.pathname + urlObj.search;

      const response = await api.get(relativePath);
      let fetchedNotifications = response.data["results"];

      // Get user's subscription settings
      const subscriptions = notificationService.getLocalSubscriptions();

      // Filter notifications based on subscribed categories
      const filteredNotifications = fetchedNotifications.filter(notification => {
        const category = notification.category;
        // If no subscriptions data, show all notifications
        if (Object.keys(subscriptions).length === 0) return true;
        // Show notification if category is subscribed (default to true if not set)
        return subscriptions[category] !== false;
      });

      setNotifications(filteredNotifications);
      setNextPage(response.data["next"]);
      setPreviousPage(response.data["previous"]);

      // Set current page based on the fetched URL's page parameter
      const params = new URLSearchParams(urlObj.search);
      const page = params.get('page');
      if (page) {
        setCurrentPage(parseInt(page));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 404) {
        // If we get a 404, it means there's no more data in that direction
        // Update the pagination state to disable the button
        if (direction === 'next') {
          setNextPage(null);
        } else if (direction === 'previous') {
          setPreviousPage(null);
        }
        // Don't show error toast for 404 - just disable the button
      } else {
        toast.error("Failed to load notifications.");
      }
    }
  };

  const hasSelection = isAllSelected || selectedNotifications.length > 0;

  return (
    <div className="Notifications-page">
      <div className="Notifications">
        <h1>Notifications</h1>
        {hasSelection && (
          <div className="buttons">
            <SecondaryButton
              onClick={handleMarkAsRead}
              buttonText='Mark as Read'
            />
            <PrimaryButton onClick={handleDelete}
              buttonText='Delete Notification'
            />
            {isAllSelected && (
              <PrimaryButton onClick={handleDeleteAll}
                buttonText='Delete All Notifications'
              />
            )}
          </div>
        )}
      </div>

      {notifications.length > 0 ? (
        <div className="notification-board">
          <div className="notification-select-all">
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              id="select-all-notifications"
            />
            <label htmlFor="select-all-notifications">Select All</label>
          </div>

          <NotificationList
            notifications={notifications}
            selectedNotifications={selectedNotifications}
            handleSelect={handleSelect}
            handleSelectAll={handleSelectAll}
            isAllSelected={isAllSelected}
            formatDate={formatNotificationDate}
          />
        </div>
      ) : (
        <EmptyState
          icon="notification"
          title="No notifications found"
          message="You're all caught up! There are no notifications to display."
        />
      )}

      <div className="pagination-controls">
        <button
          className={`previous ${!previousPage ? 'disabled' : ''}`}
          onClick={() => changePage(previousPage, 'previous')}
          disabled={!previousPage}
        >
          Previous
        </button>
        <span className="page-info">Page {currentPage}</span>
        <button
          className={`next ${!nextPage ? 'disabled' : ''}`}
          onClick={() => changePage(nextPage, 'next')}
          disabled={!nextPage}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const Notifications = () => {
  const [unReadCount, setUnreadCount] = useState(0);
  const handleUnreadCountUpdate = (newCount) => {
    setUnreadCount(newCount);
  };

  return (
    <DashboardContainer content={<NotificationPageContent updateUnreadCount={handleUnreadCountUpdate} />} pageTitle={'Notifications'} />
  );
};

export default Notifications;
